import type { RootState } from '@src/store/store';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import type { ApiError } from 'src/lib/axios';
import type {
  Communication,
  TestSendResult,
  CommunicationStatus,
  CommunicationTargeting,
  CommunicationAuditEvent,
  CommunicationStatistics,
  CreateCommunicationInput,
  UpdateCommunicationInput,
  CommunicationDeliveryState,
  CommunicationDeliveriesPage,
} from 'src/types/communication';

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import api, { endpoints } from 'src/lib/axios';

// ----------------------------------------------------------------------

interface CommunicationsState {
  data: Communication[];
  current: Communication | null;
  statistics: CommunicationStatistics | null;
  deliveries: CommunicationDeliveriesPage | null;
  audit: CommunicationAuditEvent[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string;
}

export const initialState: CommunicationsState = {
  data: [],
  current: null,
  statistics: null,
  deliveries: null,
  audit: [],
  status: 'idle',
  error: '',
};

const base = endpoints.admin.communications;

/**
 * The message the admin should see. The axios interceptor rejects with an
 * Error that keeps the envelope's `details`, so a class-validator rejection
 * names its first failed constraint instead of a bare « Validation failed ».
 */
const messageOf = (error: unknown): string => {
  const apiError = error as Partial<ApiError> | undefined;
  const constraint = apiError?.details?.[0]?.constraints?.[0];
  return constraint || apiError?.message || 'Request failed';
};

// ----------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------

export const loadCommunications = createAsyncThunk<
  Communication[],
  { search?: string; status?: CommunicationStatus } | void
>('communications/load', async (params) => {
  const query = new URLSearchParams();
  if (params?.search) query.append('search', params.search);
  if (params?.status) query.append('status', params.status);
  const url = query.toString() ? `${base}?${query.toString()}` : base;
  const response = await api.get<Communication[]>(url);
  return response.data;
});

export const loadCommunication = createAsyncThunk<Communication, string, { rejectValue: string }>(
  'communications/loadOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get<Communication>(`${base}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(messageOf(error));
    }
  }
);

export const loadCommunicationStatistics = createAsyncThunk<CommunicationStatistics>(
  'communications/loadStatistics',
  async () => {
    const response = await api.get<CommunicationStatistics>(`${base}/statistics`);
    return response.data;
  }
);

export const createCommunication = createAsyncThunk<
  Communication,
  CreateCommunicationInput,
  { rejectValue: string }
>('communications/create', async (data, { rejectWithValue }) => {
  try {
    const response = await api.post<Communication>(base, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(messageOf(error));
  }
});

export const updateCommunication = createAsyncThunk<
  Communication,
  { id: string; data: UpdateCommunicationInput },
  { rejectValue: string }
>('communications/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await api.patch<Communication>(`${base}/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(messageOf(error));
  }
});

export const deleteCommunication = createAsyncThunk<string, string, { rejectValue: string }>(
  'communications/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${base}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(messageOf(error));
    }
  }
);

export const sendCommunication = createAsyncThunk<Communication, string, { rejectValue: string }>(
  'communications/send',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post<Communication>(`${base}/${id}/send`);
      return response.data;
    } catch (error) {
      return rejectWithValue(messageOf(error));
    }
  }
);

export const scheduleCommunication = createAsyncThunk<
  Communication,
  { id: string; scheduledFor: string },
  { rejectValue: string }
>('communications/schedule', async ({ id, scheduledFor }, { rejectWithValue }) => {
  try {
    const response = await api.post<Communication>(`${base}/${id}/schedule`, { scheduledFor });
    return response.data;
  } catch (error) {
    return rejectWithValue(messageOf(error));
  }
});

export const cancelCommunication = createAsyncThunk<Communication, string, { rejectValue: string }>(
  'communications/cancel',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post<Communication>(`${base}/${id}/cancel`);
      return response.data;
    } catch (error) {
      return rejectWithValue(messageOf(error));
    }
  }
);

export const testSendCommunication = createAsyncThunk<
  TestSendResult,
  { id: string; username: string },
  { rejectValue: string }
>('communications/testSend', async ({ id, username }, { rejectWithValue }) => {
  try {
    const response = await api.post<TestSendResult>(`${base}/${id}/test-send`, { username });
    return response.data;
  } catch (error) {
    return rejectWithValue(messageOf(error));
  }
});

/** Not stored: the composer reads it straight back for the live count. */
export const previewCommunicationAudience = createAsyncThunk<number, CommunicationTargeting>(
  'communications/previewAudience',
  async (targeting) => {
    const response = await api.post<{ count: number }>(`${base}/preview-audience`, targeting);
    return response.data.count;
  }
);

export const loadCommunicationDeliveries = createAsyncThunk<
  CommunicationDeliveriesPage,
  { id: string; state?: CommunicationDeliveryState; page?: number; limit?: number }
>('communications/loadDeliveries', async ({ id, state, page, limit }) => {
  const query = new URLSearchParams();
  if (state) query.append('state', state);
  if (page) query.append('page', String(page));
  if (limit) query.append('limit', String(limit));
  const response = await api.get<CommunicationDeliveriesPage>(
    `${base}/${id}/deliveries?${query.toString()}`
  );
  return response.data;
});

export const loadCommunicationAudit = createAsyncThunk<CommunicationAuditEvent[], string>(
  'communications/loadAudit',
  async (id) => {
    const response = await api.get<CommunicationAuditEvent[]>(`${base}/${id}/audit`);
    return response.data;
  }
);

// ----------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------

const replaceInList = (state: Draft<CommunicationsState>, communication: Communication) => {
  const index = state.data.findIndex((c) => c.id === communication.id);
  if (index !== -1) state.data[index] = communication;
  if (state.current?.id === communication.id) state.current = communication;
};

const slice = createSlice({
  name: 'communications',
  initialState,
  reducers: {
    clearCurrentCommunication: (state: Draft<CommunicationsState>) => {
      state.current = null;
      state.deliveries = null;
      state.audit = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCommunications.pending, (state: Draft<CommunicationsState>) => {
        state.status = 'loading';
      })
      .addCase(
        loadCommunications.fulfilled,
        (state: Draft<CommunicationsState>, action: PayloadAction<Communication[]>) => {
          state.status = 'succeeded';
          state.data = action.payload;
          state.error = '';
        }
      )
      .addCase(loadCommunications.rejected, (state: Draft<CommunicationsState>, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to load communications';
      })
      .addCase(
        loadCommunication.fulfilled,
        (state: Draft<CommunicationsState>, action: PayloadAction<Communication>) => {
          state.current = action.payload;
          state.error = '';
        }
      )
      .addCase(loadCommunication.rejected, (state: Draft<CommunicationsState>, action) => {
        // The views toast this and leave; nothing to spin on.
        state.error = action.payload ?? action.error.message ?? 'Failed to load the communication';
      })
      .addCase(
        loadCommunicationStatistics.fulfilled,
        (state: Draft<CommunicationsState>, action: PayloadAction<CommunicationStatistics>) => {
          state.statistics = action.payload;
        }
      )
      .addCase(
        loadCommunicationDeliveries.fulfilled,
        (state: Draft<CommunicationsState>, action: PayloadAction<CommunicationDeliveriesPage>) => {
          state.deliveries = action.payload;
        }
      )
      .addCase(
        loadCommunicationAudit.fulfilled,
        (state: Draft<CommunicationsState>, action: PayloadAction<CommunicationAuditEvent[]>) => {
          state.audit = action.payload;
        }
      )
      .addCase(
        createCommunication.fulfilled,
        (state: Draft<CommunicationsState>, action: PayloadAction<Communication>) => {
          state.data.unshift(action.payload);
          state.current = action.payload;
        }
      )
      .addCase(
        deleteCommunication.fulfilled,
        (state: Draft<CommunicationsState>, action: PayloadAction<string>) => {
          state.data = state.data.filter((c) => c.id !== action.payload);
          if (state.current?.id === action.payload) state.current = null;
        }
      );

    // Every transition answers with the updated row.
    for (const thunk of [
      updateCommunication,
      sendCommunication,
      scheduleCommunication,
      cancelCommunication,
    ]) {
      builder.addCase(
        thunk.fulfilled,
        (state: Draft<CommunicationsState>, action: PayloadAction<Communication>) => {
          replaceInList(state, action.payload);
        }
      );
    }
  },
});

export const { clearCurrentCommunication } = slice.actions;
export const communicationsReducer = slice.reducer;

// ----------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------

export const selectCommunications = (state: RootState) => state.communications;
export const selectCurrentCommunication = (state: RootState) => state.communications.current;
export const selectCommunicationStatistics = (state: RootState) => state.communications.statistics;
export const selectCommunicationDeliveries = (state: RootState) => state.communications.deliveries;
export const selectCommunicationAudit = (state: RootState) => state.communications.audit;
